import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './supabase/database.types';

/**
 * Every read the slice performs.
 *
 * **There is no tenant filter in this file, and there must never be one.** Not
 * one `.eq('coach_id', …)`, not one `.eq('client_id', …)`, not the slug from the
 * URL. Row-level security supplies the tenant predicate on every one of these
 * queries; if a query needed a filter here to be correct, the policy would be
 * wrong and the next surface to forget the filter would leak.
 *
 * That is what makes the tests in `tests/rls/` meaningful: they call these exact
 * functions with a real client's token and a foreign row's id, and the answer
 * has to come back empty.
 */

export type AppClient = SupabaseClient<Database>;

type Tables = Database['public']['Tables'];
export type WeekRow = Tables['week']['Row'];
export type SessionRow = Tables['session']['Row'];
export type ExerciseEntryRow = Tables['exercise_entry']['Row'];
export type ClientRow = Tables['client']['Row'];
export type CoachInstanceRow = Tables['coach_instance']['Row'];
export type PlanRow = Tables['plan']['Row'];

interface Result<T> {
  data: T;
  error: { message: string } | null;
}

/** PostgREST errors are values, not exceptions; make them exceptions here. */
function unwrap<T>(result: Result<T>): T {
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

/** A list query denied by policy comes back as an empty list, never as null. */
function unwrapList<T>(result: Result<T[] | null>): T[] {
  return unwrap(result) ?? [];
}

/** The instance the caller belongs to, coach or client. */
export async function getMyInstance(client: AppClient): Promise<CoachInstanceRow | null> {
  return unwrap(await client.from('coach_instance').select('*').maybeSingle());
}

/** The coach's roster. Returns [] for a client, because the policy says so. */
export async function listRoster(client: AppClient): Promise<ClientRow[]> {
  return unwrapList(await client.from('client').select('*').order('display_name'));
}

/** The caller's own client row, or null when the caller is not a client. */
export async function getMyClientRow(client: AppClient): Promise<ClientRow | null> {
  return unwrap(await client.from('client').select('*').maybeSingle());
}

export async function listPlans(client: AppClient): Promise<PlanRow[]> {
  return unwrapList(await client.from('plan').select('*').order('created_at'));
}

/** Every week the caller may see. For a client that is the published ones. */
export async function listWeeks(client: AppClient): Promise<WeekRow[]> {
  return unwrapList(await client.from('week').select('*').order('week_number'));
}

/**
 * One week by id, or `null`.
 *
 * `null` is load-bearing: it is what the UI renders as "Page not found". A
 * foreign week, a draft week and a week that does not exist must all produce
 * exactly this, with no error to distinguish them — `access-and-consent`
 * requires "no hint that the content exists".
 */
export async function getWeek(client: AppClient, weekId: string): Promise<WeekRow | null> {
  return unwrap(await client.from('week').select('*').eq('id', weekId).maybeSingle());
}

export async function getSession(client: AppClient, sessionId: string): Promise<SessionRow | null> {
  return unwrap(await client.from('session').select('*').eq('id', sessionId).maybeSingle());
}

export async function listSessions(client: AppClient, weekId: string): Promise<SessionRow[]> {
  return unwrapList(
    await client.from('session').select('*').eq('week_id', weekId).order('day_of_week'),
  );
}

export async function listEntries(
  client: AppClient,
  sessionId: string,
): Promise<ExerciseEntryRow[]> {
  return unwrapList(
    await client.from('exercise_entry').select('*').eq('session_id', sessionId).order('position'),
  );
}

/* -------------------------------------------------------- the catalogue ---
 *
 * `exercise_catalog` is the one table in the schema with no tenant predicate to
 * supply: it is reference data, identical in every instance, and its policy is
 * `using (true)`. That is not an exception to the rule at the top of this file —
 * it is the same rule. The policy still decides what comes back; it simply has
 * nothing to narrow.
 *
 * The columns are named rather than `*`, and the omission is deliberate:
 * `instructions` carries every step in ten languages and is ten times the size
 * of everything else. The picker never needs it, and the client's "How to do
 * it" reads one row at a time.
 */

/* ---------------------------------------------------------- templates ---
 *
 * The template tree is the coach's and nobody else's. There is no tenant filter
 * here either, and there is not even a client policy on the tables to narrow
 * against: a client calling these gets an empty list because nothing grants her
 * a row, which is the property `tests/rls/isolation.test.ts` asserts over HTTP
 * with her own token.
 */

export type PlanTemplateRow = Tables['plan_template']['Row'];

/** Every template of the signed-in coach, most recently used first. */
export async function listTemplates(client: AppClient): Promise<PlanTemplateRow[]> {
  return unwrapList(
    await client
      .from('plan_template')
      .select('*')
      .order('last_used_at', { ascending: false, nullsFirst: false })
      .order('name'),
  );
}

/**
 * One template by id, or `null`.
 *
 * `null` for the same reason `getWeek` returns it: another instance's template
 * must be indistinguishable from one that never existed. An error here would be
 * a hint that the content exists.
 */
export async function getTemplate(
  client: AppClient,
  templateId: string,
): Promise<PlanTemplateRow | null> {
  return unwrap(await client.from('plan_template').select('*').eq('id', templateId).maybeSingle());
}

/**
 * How many weeks each of the coach's templates holds, keyed by template id.
 *
 * There is no `week_count` column and deliberately so: a stored count is a
 * second copy of a fact the rows already state, and the first time the two
 * disagree the list lies about what the coach is about to apply. So the weeks
 * are counted from the weeks.
 *
 * One request for every template rather than one per template, and the rows are
 * `template_id` alone — at a dozen templates of a handful of weeks each this is
 * a few dozen uuids, which is cheaper than the round trips a per-template count
 * would cost. A template with no weeks is simply absent from the map, and the
 * caller reads that as zero.
 */
export async function countTemplateWeeks(client: AppClient): Promise<Map<string, number>> {
  const rows = unwrapList(await client.from('plan_template_week').select('template_id'));
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.template_id, (counts.get(row.template_id) ?? 0) + 1);
  return counts;
}

export type ExerciseCatalogRow = Tables['exercise_catalog']['Row'];
export type ExerciseLibraryItemRow = Tables['exercise_library_item']['Row'];
export type ExerciseLibraryItemInsert = Tables['exercise_library_item']['Insert'];

const CATALOG_PICKER_COLUMNS =
  'id, slug, name, equipment, body_part, target, muscle_group, secondary_muscles, media_key';

export type CatalogPickerRow = Pick<
  ExerciseCatalogRow,
  | 'id'
  | 'slug'
  | 'name'
  | 'equipment'
  | 'body_part'
  | 'target'
  | 'muscle_group'
  | 'secondary_muscles'
  | 'media_key'
>;

/** Every stock exercise, without its instructions. Around 165 rows. */
export async function listCatalogue(client: AppClient): Promise<CatalogPickerRow[]> {
  return unwrapList(
    await client.from('exercise_catalog').select(CATALOG_PICKER_COLUMNS).order('name'),
  );
}

/**
 * The stock exercises behind a page of plan entries, in full.
 *
 * One request for the whole page rather than one per exercise, and it returns
 * whatever exists: a catalogue row that has been deleted is simply absent, and
 * the caller renders the entry without a description. That is the behaviour the
 * expiry depends on, so it is the ordinary path rather than an error path.
 */
export async function listCatalogueByIds(
  client: AppClient,
  ids: readonly string[],
): Promise<ExerciseCatalogRow[]> {
  if (ids.length === 0) return [];
  return unwrapList(
    await client
      .from('exercise_catalog')
      .select('*')
      .in('id', [...ids]),
  );
}

/** One stock exercise in full, including how it is performed. */
export async function getCatalogueEntry(
  client: AppClient,
  catalogId: string,
): Promise<ExerciseCatalogRow | null> {
  return unwrap(
    await client.from('exercise_catalog').select('*').eq('id', catalogId).maybeSingle(),
  );
}

/** The coach's own exercises. Empty for a client, because the policy says so. */
export async function listLibraryItems(client: AppClient): Promise<ExerciseLibraryItemRow[]> {
  return unwrapList(await client.from('exercise_library_item').select('*').order('name'));
}

/**
 * The library items behind a set of entries, for a client's session screen.
 *
 * Her policy lets her read an item only when one of her own published entries
 * points at it, so this returns her coach's cue for the exercises she has been
 * given and nothing else. The `in` list is not a tenant filter: it is the page
 * asking for the rows it is about to render.
 */
export async function listLibraryItemsByIds(
  client: AppClient,
  ids: readonly string[],
): Promise<ExerciseLibraryItemRow[]> {
  if (ids.length === 0) return [];
  return unwrapList(
    await client
      .from('exercise_library_item')
      .select('*')
      .in('id', [...ids]),
  );
}

/**
 * The exercises the coach used most recently, newest first and deduplicated by
 * name, so that repeating last week's movement costs no typing.
 *
 * Read from the entries she has actually written rather than from a "last used"
 * column, because an entry she typed by hand is every bit as recently used as
 * one she picked, and a column would only know about the second kind.
 */
export async function listRecentEntries(
  client: AppClient,
  limit = 60,
): Promise<Pick<ExerciseEntryRow, 'name' | 'library_item_id'>[]> {
  return unwrapList(
    await client
      .from('exercise_entry')
      .select('name, library_item_id')
      .order('created_at', { ascending: false })
      .limit(limit),
  );
}

/* ------------------------------------------------- the coach, in person ---
 *
 * Two of the three things that make this a coaching product rather than a
 * training document — what she has written for this client, and the one thread
 * between them — **have no table behind them**.
 *
 * `recommendation` does not exist: it is a capability in `mvp-discovery` and
 * nothing more. `message` does not exist either, and `client-coach-presence`
 * adds neither, deliberately (its design, Non-Goals: "Any migration, table,
 * policy or storage bucket").
 *
 * So these two functions answer with nothing, and that is the **truth** rather
 * than a stub: in a build served by a real database there are no notes and
 * there are no messages, because there is nowhere for one to be. The screens
 * above them therefore render their empty states, which is exactly what a real
 * client would see today.
 *
 * They live here, in the seam, rather than inside the fixture module, for the
 * reason `src/shared/queries.source.ts` gives at length: the two modules must
 * have identical signatures — `tests/unit/fixtureContract.test.ts` proves it in
 * both directions — so a screen cannot tell which of them answered it and no
 * screen branches on the demo flag. The invented notes and messages live in
 * `src/shared/fixtures/data.ts` with every other invented thing, where gate item
 * B9 can delete them in one stroke.
 *
 * Neither takes a tenant filter, for the same reason nothing else in this file
 * does — but note that neither takes the database handle *anywhere* either, so
 * there is nothing here for a policy to narrow yet. When the tables arrive, the
 * bodies become ordinary selects and the callers do not move.
 */

/**
 * A note the coach has written for one client — a published recommendation.
 *
 * The shape is `mvp-discovery` → `recommendations`: one of exactly three types,
 * a title, a body, a publication date. There is no medical or clinical type and
 * there must never be one.
 *
 * It is an interface rather than `Tables['recommendation']['Row']` because the
 * table does not exist. When it does, this becomes that and nothing else here
 * changes.
 */
export interface CoachNoteRow {
  id: string;
  coach_id: string;
  client_id: string;
  /** Training, nutrition, recovery. No fourth kind, and never a clinical one. */
  kind: 'training' | 'nutrition' | 'recovery';
  title: string;
  body: string;
  /** When the coach published it, as an ISO instant. */
  published_at: string;
}

/**
 * One message in the single thread between a client and their coach.
 *
 * `author` is the whole of "who said this". There is no sender id and no join,
 * because the thread has exactly two ends — one coach per instance is enforced
 * by `profile_one_coach_per_instance`, and the client is whoever is reading —
 * so anything richer would be inventing a shape the product does not have.
 *
 * **What the client writes on this device never comes back through here.** There
 * is nothing to write it to, so it is held on the device by
 * `src/app/client/presence.ts`, in the open, the same way a session's ticks are,
 * and the thread screen merges the two by time.
 */
export interface ThreadMessageRow {
  id: string;
  coach_id: string;
  client_id: string;
  author: 'coach' | 'client';
  body: string;
  /** When it was sent, as an ISO instant. */
  sent_at: string;
}

/**
 * Every note the caller's coach has published for them, newest first.
 *
 * Answers with nothing, because there is no `recommendation` table. Not `async`
 * on purpose: an `async` function with nothing to await is a lint error, and the
 * promise it returns is the same promise either way — which is what lets the
 * fixture module's `async` twin stand in for it exactly.
 */
/* eslint-disable-next-line @typescript-eslint/no-unused-vars --
   The handle is taken and dropped, exactly as the fixture module's twin does.
   It is the signature that matters: a screen must not be able to tell which of
   the two answered it. When the table exists this body becomes a select and the
   parameter stops being decorative. */
export function listCoachNotes(_client: AppClient): Promise<CoachNoteRow[]> {
  return Promise.resolve([]);
}

/** Every message in the caller's one thread, oldest first. See above. */
/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- see above */
export function listThreadMessages(_client: AppClient): Promise<ThreadMessageRow[]> {
  return Promise.resolve([]);
}

/**
 * One measurement a client took of their own body.
 *
 * **There is no `body_metric` table and this change adds none** (`client-me`
 * design, Non-Goals), so this answers with nothing on real data — the same
 * arrangement as the two above. The shape is fixed here rather than in a screen
 * because it is what the fixtures have to produce and what a migration will
 * later have to match.
 *
 * Four fields and no fifth. In particular there is **no photograph, no computed
 * index and no target**: counsel's body-metrics ruling forbids photos, body fat,
 * BMI, any computed classification, coach-set targets and ideal-weight
 * suggestions, and the cheapest way to keep a forbidden field out of a product
 * is to leave it out of the type. `metric` is one of the fixed set in
 * `src/app/client/measurements.ts`; there is no free-text metric name, so the
 * set cannot quietly grow a sixth entry that nobody reviewed.
 *
 * `measured_on` is a calendar day, not an instant: a weight belongs to a
 * morning, not to 07:14:32, and a day survives the client travelling.
 */
export interface BodyMetricRow {
  id: string;
  coach_id: string;
  client_id: string;
  /** One of the fixed set. Never free text. */
  metric: string;
  value: number;
  /** The day it is against, as `YYYY-MM-DD`. */
  measured_on: string;
}

/**
 * Every measurement the caller has recorded, oldest first.
 *
 * Answers with nothing, because the table does not exist. What a client enters
 * on this device is held by `src/app/client/measurements.ts`, in the open, the
 * same way a session's ticks and a message they typed are — and the screen says
 * so rather than letting it be discovered.
 */
/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- see above */
export function listBodyMetrics(_client: AppClient): Promise<BodyMetricRow[]> {
  return Promise.resolve([]);
}

/* ------------------------------------------------------------------ writes ---
 *
 * A write is not a read. Reads carry no tenant predicate — row-level security
 * supplies it. Inserts, on the other hand, must *state* their anchors, because
 * `coach_id` and `client_id` are `not null` columns: the policy's `with check`
 * then compares what was stated against `app.current_coach_id()` and throws
 * 42501 when they disagree. Stating an anchor is therefore the opposite of
 * trusting the caller: it is what gives the database something to refuse.
 *
 * Narrowing a read to one client (`.eq('client_id', …)`) is likewise not a
 * tenant filter. It is the coach choosing which of *her* clients to look at,
 * inside a policy that has already fenced her to her own instance.
 */

export type ProfileRow = Tables['profile']['Row'];
export type PlanInsert = Tables['plan']['Insert'];
export type WeekInsert = Tables['week']['Insert'];
export type SessionInsert = Tables['session']['Insert'];
export type ExerciseEntryInsert = Tables['exercise_entry']['Insert'];

/**
 * The caller's own profile row.
 *
 * Filtered by user id, which is identity rather than tenancy: a coach's policy
 * lets her read every profile in her instance, and this asks for exactly one.
 * Returns `null` for a client who has verified their invitation link but has
 * not been redeemed yet — a real state, handled on the welcome page.
 */
export async function getMyProfile(client: AppClient, userId: string): Promise<ProfileRow | null> {
  return unwrap(await client.from('profile').select('*').eq('id', userId).maybeSingle());
}

/** One client of the signed-in coach, or `null` — including for a foreign id. */
export async function getClient(client: AppClient, clientId: string): Promise<ClientRow | null> {
  return unwrap(await client.from('client').select('*').eq('id', clientId).maybeSingle());
}

export async function listPlansForClient(client: AppClient, clientId: string): Promise<PlanRow[]> {
  return unwrapList(
    await client.from('plan').select('*').eq('client_id', clientId).order('created_at'),
  );
}

export async function createPlan(client: AppClient, plan: PlanInsert): Promise<PlanRow> {
  const row = unwrap(await client.from('plan').insert(plan).select('*').single());
  if (!row) throw new Error('the plan was not created');
  return row;
}

export async function listWeeksForClient(client: AppClient, clientId: string): Promise<WeekRow[]> {
  return unwrapList(
    await client.from('week').select('*').eq('client_id', clientId).order('week_number'),
  );
}

export async function createWeek(client: AppClient, week: WeekInsert): Promise<WeekRow> {
  const row = unwrap(await client.from('week').insert(week).select('*').single());
  if (!row) throw new Error('the week was not created');
  return row;
}

/**
 * Publish a week.
 *
 * `status` and `published_at` move together, and the table's own check
 * constraint (`(status = 'published') = (published_at is not null)`) is what
 * makes that true even for a write this function never made.
 */
export async function publishWeek(client: AppClient, weekId: string): Promise<WeekRow> {
  const row = unwrap(
    await client
      .from('week')
      .update({ status: 'published', published_at: new Date().toISOString() })
      .eq('id', weekId)
      .select('*')
      .single(),
  );
  if (!row) throw new Error('the week was not published');
  return row;
}

/**
 * Duplicate a week inside its own plan, and return the new week's id.
 *
 * One call, not a loop over weeks, sessions and entries from here: the whole
 * copy is one statement, so a failure part-way through leaves nothing behind.
 * An application-side loop is how a client ends up with two of a four-week block
 * and a coach who does not know it.
 *
 * No anchors are passed. The function reads the coach and the client from the
 * plan it is writing into, and row-level security decides whether the caller may
 * read the source week at all — so a week id belonging to another instance comes
 * back as a refusal that says only "week not found".
 */
export async function duplicateWeek(client: AppClient, weekId: string): Promise<string> {
  const newWeekId = unwrap(await client.rpc('copy_week_within_plan', { p_week_id: weekId }));
  if (!newWeekId) throw new Error('the week was not duplicated');
  return newWeekId;
}

/**
 * Save a client's plan as a new template, and return the new template's id.
 *
 * The name is trimmed and length-checked by the database — `check (length(btrim
 * (name)) between 1 and 80)` — so a name the form let through by accident is
 * still refused, and the form's own check is a courtesy rather than the rule.
 *
 * What is carried and what is left behind is decided inside the function, not
 * here: every load, every session note written to that one client, every week
 * start date and all publish state stay with the client they belong to. A caller
 * cannot opt out of that, which is the point of it living in one statement.
 */
export async function savePlanAsTemplate(
  client: AppClient,
  planId: string,
  name: string,
): Promise<string> {
  const templateId = unwrap(
    await client.rpc('save_plan_as_template', { p_plan_id: planId, p_name: name }),
  );
  if (!templateId) throw new Error('the template was not created');
  return templateId;
}

/**
 * Copy a template's weeks into a client's plan as drafts, and return how many
 * weeks were created.
 *
 * **The plan keeps no reference to the template.** Nothing is written that could
 * later be read back as "this came from that": there is no column for it, by
 * design, and there is nothing for this function to pass. What the coach gets is
 * weeks, and they are hers to edit from that moment on.
 */
export async function startPlanFromTemplate(
  client: AppClient,
  templateId: string,
  planId: string,
): Promise<number> {
  const created = unwrap(
    await client.rpc('start_plan_from_template', {
      p_template_id: templateId,
      p_plan_id: planId,
    }),
  );
  // The function returns a count and raises rather than returning nothing, so a
  // null here is a shape nobody expects; it is refused rather than shown to the
  // coach as "null weeks added".
  if (created === null) throw new Error('the template was not applied');
  return created;
}

/** Rename a template. `name` is the only column an update is granted on it. */
export async function renameTemplate(
  client: AppClient,
  templateId: string,
  name: string,
): Promise<PlanTemplateRow> {
  const row = unwrap(
    await client.from('plan_template').update({ name }).eq('id', templateId).select('*').single(),
  );
  if (!row) throw new Error('the template was not renamed');
  return row;
}

/**
 * Delete a template and everything inside it.
 *
 * Nothing else goes with it. The weeks a plan received from this template are
 * ordinary weeks of that plan — no foreign key, no cascade, nothing to follow —
 * so every plan ever started from it is untouched, including in the client's
 * view of it.
 */
export async function deleteTemplate(client: AppClient, templateId: string): Promise<void> {
  unwrap(await client.from('plan_template').delete().eq('id', templateId).select('id'));
}

export async function createSession(
  client: AppClient,
  session: SessionInsert,
): Promise<SessionRow> {
  const row = unwrap(await client.from('session').insert(session).select('*').single());
  if (!row) throw new Error('the session was not created');
  return row;
}

/**
 * What the coach is told when a removal removed nothing.
 *
 * **A refusal is not an error.** A DELETE whose policy predicate matches no row
 * affects nothing, and PostgREST answers 200 with `[]` rather than 403 or 404 —
 * `error` is null. Without the empty-array check below, `unwrap` would return
 * happily, the mutation's `onSuccess` would run, the list would refetch, and the
 * row would still be sitting there with nothing said about it.
 *
 * The message names both possibilities instead of picking one, because at this
 * layer they are genuinely indistinguishable: the week was published in another
 * tab, or the row was already deleted in another tab. Both are `[]`. Guessing
 * would be wrong half the time, and the half it got wrong would be the half the
 * coach needed the truth about (design D5).
 */
function notRemoved(what: 'session' | 'exercise'): string {
  return `That ${what} was not removed. Its week may have been published, or it may already be gone.`;
}

/**
 * Delete a session, and with it the exercise entries inside it.
 *
 * The cascade is `exercise_entry_session_fk` and it already existed; nothing
 * else in the schema points at `session`, and `151_cascade_guard.sql` is what
 * keeps that true as the product grows.
 *
 * Whether this is *allowed* is not decided here. `session_delete_draft_only`
 * (`00015_delete_gate.sql`) refuses it while the week is published, so a coach
 * on a page opened before the week was published gets the refusal rather than
 * the deletion — which is the point of putting the rule in the database.
 */
export async function deleteSession(client: AppClient, sessionId: string): Promise<void> {
  const removed = unwrapList(
    await client.from('session').delete().eq('id', sessionId).select('id'),
  );
  if (removed.length === 0) throw new Error(notRemoved('session'));
}

/**
 * Take a published week back to draft.
 *
 * The inverse of `publishWeek`, and the only route a coach has to changing a
 * published week while the delete gate stands. `status` and `published_at` move
 * together for the same reason they do when publishing: the table's own check
 * constraint refuses any row where one is set without the other, so "draft with
 * a publication date" is not a state this application has to remember to avoid.
 *
 * Nothing is deleted. The client stops seeing the week — her policy is
 * `status = 'published'` — and sees it again when it is published again.
 */
export async function unpublishWeek(client: AppClient, weekId: string): Promise<WeekRow> {
  const row = unwrap(
    await client
      .from('week')
      .update({ status: 'draft', published_at: null })
      .eq('id', weekId)
      .select('*')
      .single(),
  );
  if (!row) throw new Error('the week was not unpublished');
  return row;
}

/**
 * Adoption: the coach takes a stock exercise and gets **her own** exercise,
 * linked to it.
 *
 * Idempotent, because `unique (coach_id, catalog_id)` says a coach adopts a
 * stock exercise once. Two tabs, or a double submit, therefore end with one row
 * rather than an error — the insert is tried, and a conflict means somebody
 * already did it and the existing row is what she wanted anyway.
 *
 * `coach_id` is stated rather than trusted: the policy's `with check` compares
 * it against `app.current_coach_id()` and throws 42501 when they disagree.
 */
export async function adoptCatalogueExercise(
  client: AppClient,
  params: { coachId: string; catalogId: string; name: string },
): Promise<ExerciseLibraryItemRow> {
  const inserted = await client
    .from('exercise_library_item')
    .insert({ coach_id: params.coachId, catalog_id: params.catalogId, name: params.name })
    .select('*')
    .maybeSingle();

  if (!inserted.error && inserted.data) return inserted.data;

  const existing = unwrap(
    await client
      .from('exercise_library_item')
      .select('*')
      .eq('catalog_id', params.catalogId)
      .maybeSingle(),
  );
  if (existing) return existing;

  throw new Error(inserted.error?.message ?? 'the exercise was not adopted');
}

/** Her name and her cue for one of her own exercises. Nothing else is granted. */
export async function updateLibraryItem(
  client: AppClient,
  itemId: string,
  fields: { name: string; cue: string | null },
): Promise<ExerciseLibraryItemRow> {
  const row = unwrap(
    await client.from('exercise_library_item').update(fields).eq('id', itemId).select('*').single(),
  );
  if (!row) throw new Error('the exercise was not updated');
  return row;
}

export async function createEntry(
  client: AppClient,
  entry: ExerciseEntryInsert,
): Promise<ExerciseEntryRow> {
  const row = unwrap(await client.from('exercise_entry').insert(entry).select('*').single());
  if (!row) throw new Error('the exercise was not added');
  return row;
}

export async function deleteEntry(client: AppClient, entryId: string): Promise<void> {
  const removed = unwrapList(
    await client.from('exercise_entry').delete().eq('id', entryId).select('id'),
  );
  if (removed.length === 0) throw new Error(notRemoved('exercise'));
}

/**
 * Reorder two entries by swapping their positions.
 *
 * Two round trips rather than one, because there is no batched update in
 * PostgREST that is also policy-checked per row. At a dozen exercises per
 * session this is the right trade.
 */
export async function swapEntryPositions(
  client: AppClient,
  a: { id: string; position: number },
  b: { id: string; position: number },
): Promise<void> {
  unwrap(
    await client
      .from('exercise_entry')
      .update({ position: b.position })
      .eq('id', a.id)
      .select('id'),
  );
  unwrap(
    await client
      .from('exercise_entry')
      .update({ position: a.position })
      .eq('id', b.id)
      .select('id'),
  );
}
